import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonFunctionsService } from '@angular/synthrone-design/services';
import { ApiService } from '@angular/synthrone-design/services/api/api.service';
import { MessagesService } from '@angular/synthrone-design/services/messages/messages.service';
import { Observable } from 'rxjs/Observable';
import { DefaultServiceSDK } from 'ms-system-angular-sdk';

@Component({
  selector: 'maintenance',
  templateUrl: './maintenance.component.html',
  styleUrls: ['./maintenance.component.scss']
})

export class MaintenanceViewComponent implements OnInit {

  @ViewChild('maintenanceTable') maintenanceTable;

  configColumns = {
    service: {
      label: 'Service Name',
      sortable: false,
      truncate: true,
      style: {
        'flex-grow': 1.5
      }
    }
  };

  maintenanceData;
  currentDate = null;
  timeOfStartFromMaintenanceData = null;
  currentTime: string;
  hours = [];
  startHour;
  maintenanceEnabled = false;
  timeSelectEnabled = true;
  isLoaded: boolean;
  canSave: boolean;
  tooltipText = 'Plan your maintenance ahead.\nMaintenance info is cached for 15 minutes - take that into consideration before applying changes.';

  triggersStatusIcons = {
    in_progress: {
      icon: 'sync',
      tooltip: 'In Progress'
    },
    done: {
      icon: 'done',
      tooltip: 'Done'
    },
    failed: {
      icon: 'error_outline',
      tooltip: 'Error'
    }
  };

  constructor(
      private systemService: DefaultServiceSDK,
      @Inject(CommonFunctionsService) public commonFunctions,
      private api: ApiService,
      private messagesService: MessagesService
  ) {
  }

  ngOnInit() {
    this.getMaintenanceConfig();
  }

  getMaintenanceConfig = () => {
    this.isLoaded = false;
    this.systemService.maintenanceGet().subscribe(result => {
      if (result) {
        this.maintenanceData = result.sort((a, b) => {
          return a.service.localeCompare(b.service)
        });
        this.checkMaintenanceStatus(result);
        this.checkTriggersStatus(result);
        this.isLoaded = true;
      }
    });
  }

  editRow = (row) => {
    if (row.workerMaintenance || row.apiMaintenance) {
      row.workerMaintenance = false;
      row.apiMaintenance = false;
    } else {
      row.workerMaintenance = true;
      row.apiMaintenance = true;
    }

    this.updateTimeSelectStatus();
  }

  getButtonText = (row) => {
    if (row.apiMaintenance === false && row.workerMaintenance === false) {
      return 'Enable both';
    }

    return 'Disable both';
  }

  toggleAll = () => {
    const currentState = !!this.maintenanceTable.data.find(item => item.workerMaintenance === false || item.apiMaintenance === false)

    this.maintenanceTable.data.forEach(item => {
      if (item.workerMaintenance !== null) {
        item.workerMaintenance = currentState;
      }

      if (item.apiMaintenance !== null) {
        item.apiMaintenance = currentState;
      }
    });

    this.updateTimeSelectStatus();
  }

  sdServices = (limit: any, offset: any) => {
    return Observable.of(this.maintenanceData);
  };

  saveMaintenanceSettings = () => {
    const startDate = !this.maintenanceEnabled ? (new Date(this.currentDate.getTime())) : (new Date(this.maintenanceData[0]['startDateTime'] * 1000));
    const startHour = this.startHour.split('-')[0];
    startDate.setHours(startHour.split(':')[0]);
    startDate.setMinutes(startHour.split(':')[1]);
    startDate.setSeconds(0);

    const params = this.maintenanceTable.data;
    params.forEach(item => {
      item.startDateTime = Math.floor(new Date(startDate).getTime() / 1000);
    });

    this.systemService.maintenancePut(params).catch((err: any) => {
      this.api.errorCallback(err).subscribe(() => { }, (parsedError) => {
        this.messagesService.showSnack(parsedError, 'error');
      });

      return Observable.throw(err);
    }).subscribe(res => {
      this.messagesService.showSnack('Maintenance config saved');
      if (res) {
        this.getMaintenanceConfig();
      }
    });
  }

  checkMaintenanceStatus = (maintenanceTableData) => {
    this.maintenanceEnabled = false;
    for (const item of maintenanceTableData) {
      if (item.apiMaintenance === true || item.workerMaintenance === true) {
        this.maintenanceEnabled = true;
        break;
      }
    }

    this.updateTimeSelect(maintenanceTableData);
  }

  checkTriggersStatus = (maintenanceTableData) => {
    this.canSave = true;
    for (const item of maintenanceTableData) {
      if (item.triggersStatus === 'in_progress') {
        this.canSave = false;
        break;
      }
    }
  }

  updateTimeSelect = (maintenanceTableData) => {
    this.hours = [];
    for (let i = 0; i < 24; i++) {
      this.hours.push(('00' + i).slice(-2) + ':00');
      this.hours.push(('00' + i).slice(-2) + ':30');
    }

    this.currentDate = new Date();
    // add current time to select
    const currentHour = ('00' + this.currentDate.getHours()).slice(-2);
    const currentMinutes = ('00' + this.currentDate.getMinutes()).slice(-2);
    this.currentTime = `${currentHour}:${currentMinutes}`;
    if (!this.hours.includes(this.currentTime)) {
      this.hours.push(this.currentTime);
      this.hours.sort();
    }

    this.timeOfStartFromMaintenanceData = new Date(this.maintenanceData[0]['startDateTime'] * 1000);
    const timeOfStartHour = ('00' + this.timeOfStartFromMaintenanceData.getHours()).slice(-2);
    const timeOfStartMinute = ('00' + this.timeOfStartFromMaintenanceData.getMinutes()).slice(-2);
    this.timeOfStartFromMaintenanceData = `${timeOfStartHour}:${timeOfStartMinute}`

    if (!this.maintenanceEnabled) {
      this.startHour = this.currentTime;
    } else {
      const indexToRemove = this.hours.findIndex(item => item === this.timeOfStartFromMaintenanceData);
      if (indexToRemove !== -1) {
        this.hours.splice(indexToRemove, 1);
      }

      const timeOfStart = `${this.timeOfStartFromMaintenanceData} - START`;
      this.hours.push(timeOfStart);
      this.hours.sort();

      this.startHour = timeOfStart;
    }

    this.updateTimeSelectStatus(maintenanceTableData);
  }

  updateTimeSelectStatus = (maintenanceTableData = null) => {
    this.timeSelectEnabled = false;

    if (this.maintenanceEnabled) {
      for (const item of maintenanceTableData ? maintenanceTableData : this.maintenanceTable.data) {
        if (item.workerMaintenance || item.apiMaintenance) {
          this.timeSelectEnabled = true;
          break;
        }
      }
    } else {
      this.timeSelectEnabled = true;
    }

    const timeOfStart = `${this.timeOfStartFromMaintenanceData} - START`;
    if (this.hours.includes(timeOfStart)) {
      this.startHour = timeOfStart;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/views/maintenance/maintenance.component.ts